import { createSelector } from '@reduxjs/toolkit';
import { SharingShape, Step, expiresValid } from './model';

import { RootSelector } from '../../models/rootSelector';
import differenceBy from 'lodash/differenceBy';
import { getTableData } from '../fileManager/selectors/getTableData';
import { CHNode, CHNodeIsFolder } from '../../../general/node';
import { negate } from 'lodash';
import { FileManagerRow } from '../fileManager/model';

export const getSharing: RootSelector<SharingShape> = (state) => state.sharing;

export const isSidebarExpanded: RootSelector<boolean> = createSelector(
  getSharing,
  (s) => s.isExpanded,
);
export const getExpiresIn: RootSelector<string> = createSelector(getSharing, (s) => s.expiresIn);

export const getName: RootSelector<string> = createSelector(getSharing, (s) => s.name);
export const getDoesNotExpire: RootSelector<boolean> = createSelector(
  getSharing,
  (s) => s.doesNotExpire,
);

export const isSidebarEnabled: RootSelector<boolean> = createSelector(
  getSharing,
  (s) => s.isEnabled,
);
export const isShareLinkLoading: RootSelector<boolean> = createSelector(
  getSharing,
  (s) => s.isShareLinkLoading,
);

export const getShareLink: RootSelector<string> = createSelector(getSharing, (s) => s.shareLink);

export const getShareSelectedNodes: RootSelector<CHNode[]> = createSelector(
  getSharing,
  (s) => s.selectedNodes,
);

export const getShareSelectedNodeIds: RootSelector<number[]> = createSelector(
  getShareSelectedNodes,
  (selectedNodes) => selectedNodes.map(({ id }) => id),
);

export const getNameWithDefault: RootSelector<string> = createSelector(
  getName,
  getShareSelectedNodes,
  (name, nodes) => {
    if (name.length > 0) {
      return name;
    }
    return nodes[0].name;
  },
);

export const getShareSelectedFolders: RootSelector<CHNode[]> = createSelector(
  getShareSelectedNodes,
  (nodes) => nodes.filter(CHNodeIsFolder),
);

export const getShareSelectedFiles: RootSelector<CHNode[]> = createSelector(
  getShareSelectedNodes,
  (nodes) => nodes.filter(negate(CHNodeIsFolder)),
);

export const getSharedLabel: RootSelector<string> = createSelector(
  [getShareSelectedFolders, getShareSelectedFiles],
  (folders, files) => {
    const fileLabel = (n: number) => (n !== 1 ? `${n} files` : `1 file`);
    const folderLabel = (n: number) => (n !== 1 ? `${n} folders` : `1 folder`);

    if (folders.length > 0 && files.length > 0) {
      return `${fileLabel(files.length)} and ${folderLabel(folders.length)}`;
    }
    if (folders.length > 0) {
      return folderLabel(folders.length);
    }
    if (files.length > 0) {
      return fileLabel(files.length);
    }
    return `nothing`;
  },
);

export const getStep: RootSelector<Step> = createSelector(
  isSidebarEnabled,
  getShareSelectedNodes,
  getShareLink,
  (enabled, selectedNodes, shareLink) => {
    // if(condition for FINISH)return Step.FINISH
    // if(condition for EMAIL)return Step.EMAIL
    if (shareLink !== '') return Step.LINK;
    if (selectedNodes.length !== 0) return Step.SELECTION;
    if (enabled) return Step.WELCOME;
    return Step.NONE;
  },
);

export const isSharingButtonEnabled: RootSelector<boolean> = createSelector(
  getStep,
  (step) => step === Step.NONE,
);

export const canAddRemoveNodes: RootSelector<boolean> = createSelector(getStep, (step) => {
  return step === Step.WELCOME || step === Step.SELECTION;
});

export const isGenShareLinkButtonEnabled: RootSelector<boolean> = createSelector(
  [getExpiresIn, isShareLinkLoading, getDoesNotExpire],
  (expires, loading, doesNotExpire) => {
    if (loading) return false;
    return expiresValid(expires) || doesNotExpire;
  },
);

export const isHeaderCellChecked: RootSelector<boolean> = createSelector(
  [getShareSelectedNodes, getTableData],
  // чекнут только если nodes ⊆ selectedNodes
  // то есть nodes ∖ selectedNodes = ∅
  (selectedNodes, nodes) =>
    selectedNodes.length > 0 && differenceBy(nodes, selectedNodes, 'id').length === 0,
);

export const getSelectedRange: RootSelector<FileManagerRow[] | null> = createSelector(
  [getTableData, getSharing],
  (sorted, shar) => {
    const { lastNodeIDSelected, firstNodeIDSelected } = shar;
    if (lastNodeIDSelected === null || firstNodeIDSelected === null) {
      return null;
    }
    const lastNodeIndex = sorted.findIndex((x) => x.id === lastNodeIDSelected);
    const firstNodeIndex = sorted.findIndex((x) => x.id === firstNodeIDSelected);
    if (lastNodeIndex === -1 || firstNodeIndex === -1) {
      return null;
    }
    const start = Math.min(lastNodeIndex, firstNodeIndex);
    const end = Math.max(lastNodeIndex, firstNodeIndex) + 1;
    return sorted.slice(start, end);
  },
);
